import * as vuelidate from "vuelidate/lib/validators";

export function build(...args) {
    var result = {};
    args.forEach(a => {
        result[a.type] = a.validator
    });
    return result;
}

let validationMessages = {};

function registerMessage(type, message) {
    if (!validationMessages[type]) {
        validationMessages[type] = message
    }
}

export function getMessage(vData) {
    if (!vData.$invalid) return null;

    var failedValidationType = _.find(_.keys(validationMessages), (type) => vData[type] === false);

    if (failedValidationType)  {
        let validationMessage = validationMessages[failedValidationType];
        if (_.isString(validationMessage)) return validationMessage;
        return validationMessage(vData);
    } else {
        return "Unknown validation error";
    }
}

function isParentValid(vueComponent) {
    if (vueComponent.$parent && !vueComponent.$parent.$v) throw "Parent component should have at least one validator";

    if (!vueComponent.$parent || vueComponent.$parent.$v.$invalid) return false;

    return true;
}

export function invalidComponent(vueComponent) {
    validateComponentAndChildren(vueComponent);

    if (includeParentInValidation(vueComponent) && !isParentValid(vueComponent)) return true;

    return hasInvalidChildrenFields(vueComponent);
}

export function resetComponent(vueComponent) {
    if (vueComponent.$parent && vueComponent.$parent.$v) vueComponent.$parent.$v.$reset();
    if (vueComponent.$children) forceChildValidationState(vueComponent.$children, "$reset");
}

function includeParentInValidation(vueComponent) {
    return (vueComponent.$vnode.componentOptions.tag == "amf-form-row");
}

function validateComponentAndChildren(vueComponent) {
    if (vueComponent.$parent && vueComponent.$parent.$v) vueComponent.$parent.$v.$touch();
    // if there are any kids, grandkids, great grandkids...etc...touch them (sounds really creepy)
    if (vueComponent.$children) forceChildValidationState(vueComponent.$children, "$touch");
}

function forceChildValidationState(children, method) {
    children.forEach(child => {
        if (child.$children.length > 0) forceChildValidationState(child.$children, method);
        if (child.$v) child.$v[method]();
        if (child.validator) child.validator[method]();
    });
};

function hasInvalidChildrenFields(vueComponent) {
    let invalidChildrenFields = function(children) {
        // iterates through each child element until an invlaid field is found...no need to keep going if there is an invalid field
        return children.some(child => {
            if (child.$v && child.$v.$invalid) return true;
            if (child.validator && child.validator.$invalid) return true;
            if (child.$children.length > 0) return invalidChildrenFields(child.$children);
        });
    };

    if (vueComponent.$children) return invalidChildrenFields(vueComponent.$children);
}

function wrap(type, message, originalValidator, param1, param2, param3) {
    registerMessage(type, message);
    let vuelidatorInstance = _.isUndefined(param1) ? originalValidator : originalValidator(param1, param2, param3);
    return {
        type: type,
        validator: vuelidatorInstance
    }
}

function hasNoValue(value){
    return typeof value === 'undefined' || value === null || value === '';
}

// ------------------------------- VALIDATORS START HERE --------------------------------------//

export function required() {
    return wrap("required", 'This field is required', vuelidate.required);
}

export function requiredIf(condition) {
    return wrap("requiredIf", 'This field is required', vuelidate.requiredIf, condition);
}

export function validateIf(condition, vuelidateType) {
    if(condition) return vuelidateType;
}

export function requiredUnless(condition) {
    return wrap("requiredUnless", "This field is required", vuelidate.requiredUnless, condition);
}

export function between(min, max) {
    return wrap("between", 
                    (vData) => `Should be between ${vData.$params.between.min} and ${vData.$params.between.max}`, 
                    vuelidate.between, 
                    min, max);
}

export function minLength(min) {
    return wrap("minLength", 
                    (vData) => `Should be at least ${vData.$params.minLength.min} characters`, 
                    vuelidate.minLength, 
                    min);
}

export function maxLength(max) {
    return wrap("maxLength", 
                    (vData) => `Should be at most ${vData.$params.maxLength.max} characters`, 
                    vuelidate.maxLength, 
                    max);
}

export function numeric() {
    return wrap("numeric", "The field must be numeric", vuelidate.numeric);
}

export function integer() {
    return wrap("integer", "The field must be a number", x => typeof x == 'number' || !isNaN(x));
}

export function decimal() {
    return wrap("decimal", "The field must be decimal", vuelidate.decimal);
}

export function email() {
    return wrap("email", "Valid email address required", vuelidate.email);
}

export function zipCode() {
    let validateZipCode = z => !z || z.length == 5 && !isNaN(z);
    return wrap("zipCode", "Zip code must be five digits", validateZipCode);
}

export function noop() {
    return wrap("noop", "Valid field required", x => true);
}

export function money(positive) {
    let validateAmount = (value) => {
        if (hasNoValue(value)) return true;
        const regex = /^[0-9]*[.]{0,1}[0-9]{0,2}$/;
        let testValue = value.toString();
        if (positive) {
            return value > 0 && regex.test(testValue);
        } else {
            return value >= 0 && regex.test(testValue);
        }
    }
    return wrap("money", "Must be valid amount", validateAmount);
}

export function upTotwoDecimalPlaces() {
    let validateAmount = (value) => {
        if (hasNoValue(value)) return true;
        const regex = /^\s*-?(\d+(\.\d{1,2})?|\.\d{1,2})\s*$/;
        let testValue = value.toString();
        return value > 0 && regex.test(testValue);
    }
    return wrap("upTotwoDecimalPlaces", "Must be greater than zero and no more than two decimal places", validateAmount);
}

export function ssn() {
    let valFunc = (value) => {
        return hasNoValue(value) || validSSN(value);
    }
    return wrap("ssn", "Valid SSN required (e.g. 123456789)", valFunc);
}

function validSSN(value){    
    return value.length == 9 && vuelidate.numeric(value) && !isSameCharacterString(0, value);
}

function isSameCharacterString(index,value)
{
    if(value == null) return false;
    if(index == value.length-1) return true;
    if(value[index] != value[index+1]) return false;
    return isSameCharacterString(index+1, value);
}

export function taxId() {
    let valFunc = (value) => {
        if (hasNoValue(value)) return true;
        value = value.replace('-', '');
        value = value.replace(' ', '');
        return value.length == 9 && vuelidate.numeric(value)
    }
    return wrap("taxId", "Valid Tax ID required (e.g. 99-9999999 or 999999999)", valFunc);
}

export function date() {
    let valFunc = (value) => {
        if (hasNoValue(value)) return true;
        var d = moment(value, 'MM/DD/YYYY', true);
        if(!d.isValid()) return false;
        return d.isBefore("9999-12-31") && d.isAfter("1753-01-01");
    }
    return wrap("date", "Valid Date required MM/DD/YYYY (e.g. 01/01/2000)", valFunc);
}

export function phone() {
    let validatePhone = value => {
        if (hasNoValue(value)) return true;
        var numericPhone = _.filter(value.split(''), c => Number.isInteger(parseInt(c))).join('');
        return numericPhone.length == 10;
    };
    return wrap("phone", `Valid number including area code required.`, validatePhone);
}

export function addressSpecialChars() {
    return wrap("address", `Address does not allow special characters: !@$%^*()={}[]|;"<>?\\`, 
        (x) => { return !x || !x.match(/[^a-zA-z0-9\,\.\-\'\#\s\/\:\&]+$/g) && x.indexOf('^') < 0});
}

export function addressLine() {
    throw new "TODO";
}

export function fileExtensions() {
    let extensions = ["pdf","jpg","jpeg","gif","png","tiff","tif","xls","xlsx","doc","docx"];

    let validateExts = fileList => {
        if(!fileList || fileList.length == 0) return true;
        extensions = extensions.map(e => e.toUpperCase());
        for(let f = 0; f < fileList.length; f++) {
            if(!extensions.find(x => x == fileList[f].name.split('.').pop().toUpperCase())) return false;
        }
        return true;
    }
    return wrap("files", "Incorrect file type. Allowed types: " + extensions.join(", "), validateExts);
}

export function custom(valFunc, message) {
    const type = "validate" + Math.random().toString().replace(".", "");
    return wrap(type, message, valFunc);
}

export function isNumberValidator(validator) {
    return validator.numeric !== undefined || validator.money !== undefined || validator.decimal != undefined;
}

export function routingNumber() {
    let valFunc = (value) => {

        return hasNoValue(value) || (value.length == 9 && vuelidate.numeric(value) && validChecksum(value));

        function validChecksum(value) {
            var n = 0;

            for (var i = 0; i < value.length; i += 3) {
                n += parseInt(value.charAt(i), 10) * 3 +
                    parseInt(value.charAt(i + 1), 10) * 7 +
                    parseInt(value.charAt(i + 2), 10);
            }

            return (n !== 0 && ((n % 10) === 0));
        }
    };
    return wrap("routingNumber", "Invalid routing number.", valFunc);
}


export function bankAccountNumber() {
    let validateBankAccountNumber = (value) => {
        return hasNoValue(value) || (value.length >= 4 && value.length < 18) && vuelidate.numeric(value);
    };
    return wrap("bankAccountNumber", `Please enter a valid numeric account number.`, validateBankAccountNumber);
}

// example of parameterized validation for the future
// const containsSymbol = (symbol) => {
//     return v.helpers.withParams( 
//         { type: "containsSymbol", symbol: symbol },
//         (value) => !v.helpers.req(value) || value.indexOf(symbol) >= 0)
// }




// WEBPACK FOOTER //
// ./src/framework/utils/validation.js